import React, { useState, useEffect, useRef } from 'react';
import './AdminLayout.css';
import logo from '../assets/ctta-logo.avif';
import { getRole, getUserInfo } from '../utils/auth';
import AdminProfileModal from './AdminProfileModal';
import AdminIcon from './AdminIcons';
import { getNavGroups, getStandaloneItems } from './adminNav';

const AdminLayout = ({ children, currentPage, onNavigate, onLogout }) => {
  const role = getRole();
  const [showProfile, setShowProfile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // flyout: the open group's second-level panel; tip: the hover label. Both are position:fixed
  // and placed from the button's own rect, because the rail scrolls (overflow) and would clip
  // anything absolutely positioned inside it.
  const [flyout, setFlyout] = useState(null);   // { key, top }
  const [tip, setTip] = useState(null);         // { label, top }
  const railRef = useRef(null);
  const flyoutRef = useRef(null);
  const userInfo = getUserInfo();
  const displayName = userInfo.display_name || userInfo.email || userInfo.username || '';
  const shownName = displayName || userInfo.username || '';

  useEffect(() => {
    if (!flyout) return undefined;
    const close = (e) => {
      if (e.type === 'keydown') {
        if (e.key === 'Escape') setFlyout(null);
        return;
      }
      const inRail = railRef.current && railRef.current.contains(e.target);
      const inFlyout = flyoutRef.current && flyoutRef.current.contains(e.target);
      if (!inRail && !inFlyout) setFlyout(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [flyout]);

  const goTo = (page) => {
    onNavigate(page);
    setMenuOpen(false);
    setFlyout(null);
    setTip(null);
  };

  const categories = getNavGroups();
  const standalone = getStandaloneItems();

  const showTip = (label) => (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2 });
  };
  const hideTip = () => setTip(null);

  const onGroupClick = (cat) => (e) => {
    setTip(null);
    // A group with a single page has no second level worth opening -- go straight there.
    if (cat.items.length === 1) { goTo(cat.items[0].page); return; }
    if (flyout && flyout.key === cat.key) { setFlyout(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const estHeight = cat.items.length * 40 + 52;
    setFlyout({ key: cat.key, top: Math.max(8, Math.min(r.top, window.innerHeight - estHeight - 8)) });
  };

  const openCat = flyout ? categories.find(c => c.key === flyout.key) : null;

  const railButton = ({ key, label, icon, active, onClick }) => (
    <button
      key={key}
      className={`admin-rail-btn ${active ? 'active' : ''}`}
      onClick={onClick}
      onMouseEnter={showTip(label)}
      onMouseLeave={hideTip}
      onFocus={showTip(label)}
      onBlur={hideTip}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
    >
      <AdminIcon name={icon} size={20} />
    </button>
  );

  return (
    <div className="admin-layout">
      {/* ---- Phones: top bar + slide-in menu with full labels (no hover on touch) ---- */}
      <header className="admin-mobile-bar">
        <button className="admin-mobile-toggle" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
          <AdminIcon name={menuOpen ? 'close' : 'menu'} size={22} />
        </button>
        <img src={logo} alt="CTTA Logo" className="admin-mobile-logo" />
        <span className="admin-mobile-title">Admin Portal</span>
      </header>
      {menuOpen && <div className="admin-scrim" onClick={() => setMenuOpen(false)} />}
      <aside className={`admin-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="admin-drawer-brand">
          <img src={logo} alt="CTTA Logo" className="admin-drawer-logo" />
          <div className="admin-drawer-brand-text">
            <span className="admin-drawer-name">Central Texas Tamil Academy</span>
            <span className="admin-drawer-sub">Admin Portal</span>
          </div>
        </div>
        <nav className="admin-drawer-nav">
          <button className={`admin-drawer-link ${currentPage === 'admin-home' ? 'active' : ''}`} onClick={() => goTo('admin-home')}>
            <AdminIcon name="home" /><span>Home</span>
          </button>
          {standalone.map(item => (
            <button key={item.page} className={`admin-drawer-link ${currentPage === item.page ? 'active' : ''}`} onClick={() => goTo(item.page)}>
              <AdminIcon name={item.icon} /><span>{item.label}</span>
            </button>
          ))}
          {categories.map(cat => (
            <div className="admin-drawer-section" key={cat.key}>
              <div className="admin-drawer-heading">{cat.label}</div>
              {cat.items.map(item => (
                <button
                  key={item.page}
                  className={`admin-drawer-link ${currentPage === item.page ? 'active' : ''}`}
                  onClick={() => goTo(item.page)}
                >
                  <AdminIcon name={item.icon} /><span>{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="admin-drawer-footer">
          <button className="admin-drawer-link" onClick={() => setShowProfile(true)}>
            <span className="admin-avatar">{(shownName || '?')[0].toUpperCase()}</span>
            <span className="admin-drawer-profile">{shownName}<small>{role.replace('_', ' ')}</small></span>
          </button>
          <button className="admin-drawer-link admin-drawer-logout" onClick={onLogout}>
            <AdminIcon name="logout" /><span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ---- Desktop: slim icon rail; hover = name, click = second level ---- */}
      <aside className="admin-rail" ref={railRef}>
        <img src={logo} alt="CTTA Logo" className="admin-rail-logo" title="Central Texas Tamil Academy" />
        <nav className="admin-rail-nav">
          {railButton({ key: 'home', label: 'Home', icon: 'home', active: currentPage === 'admin-home', onClick: () => goTo('admin-home') })}
          {standalone.map(item => railButton({ key: item.page, label: item.label, icon: item.icon, active: currentPage === item.page, onClick: () => goTo(item.page) }))}
          <div className="admin-rail-divider" />
          {categories.map(cat => railButton({
            key: cat.key, label: cat.label, icon: cat.icon,
            active: cat.items.some(i => i.page === currentPage) || (flyout && flyout.key === cat.key),
            onClick: onGroupClick(cat),
          }))}
        </nav>
        <div className="admin-rail-footer">
          <button
            className="admin-avatar admin-avatar-btn"
            onClick={() => setShowProfile(p => !p)}
            onMouseEnter={showTip(`${shownName} (${role.replace('_', ' ')})`)}
            onMouseLeave={hideTip}
            aria-label="Profile"
          >
            {(shownName || '?')[0].toUpperCase()}
          </button>
          <button
            className="admin-rail-btn admin-rail-logout"
            onClick={onLogout}
            onMouseEnter={showTip('Logout')}
            onMouseLeave={hideTip}
            aria-label="Logout"
          >
            <AdminIcon name="logout" size={20} />
          </button>
        </div>
      </aside>

      {tip && !flyout && <div className="admin-tip" style={{ top: tip.top }}>{tip.label}</div>}

      {openCat && (
        <div className="admin-flyout" ref={flyoutRef} style={{ top: flyout.top }} role="menu">
          <div className="admin-flyout-heading">{openCat.label}</div>
          {openCat.items.map(item => (
            <button
              key={item.page}
              role="menuitem"
              className={`admin-flyout-link ${currentPage === item.page ? 'active' : ''}`}
              onClick={() => goTo(item.page)}
            >
              <AdminIcon name={item.icon} size={16} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {showProfile && <AdminProfileModal onClose={() => setShowProfile(false)} />}

      <main className="admin-main-content">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
